import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronRight, X } from "lucide-react";
import { splitFolds } from "../../help/helpIndex";
import ApkLink from "./ApkLink";

/** A screenshot: full width, bordered, tap to see it bigger. */
const HelpImage = ({ src, alt }) => {
  const [big, setBig] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setBig(true)}
        aria-label={`Enlarge: ${alt}`}
        className="mb-3 block w-full overflow-hidden rounded-xl border border-border bg-black last:mb-0"
      >
        <img src={src} alt={alt} loading="lazy" className="block w-full" />
      </button>
      {big && (
        <div
          role="dialog"
          aria-label={alt}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setBig(false)}
        >
          <button
            type="button"
            aria-label="Close"
            className="absolute right-3 top-3 rounded-full p-2 text-white/70 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
          <img src={src} alt={alt} className="max-h-full max-w-full rounded-lg" />
        </div>
      )}
    </>
  );
};

const COMPONENTS = {
  p: ({ node, ...props }) => {
    const only = node?.children?.length === 1 && node.children[0].tagName === "img";
    if (only) return <>{props.children}</>;
    return <p className="mb-3 text-[15px] leading-relaxed text-foreground/90 last:mb-0" {...props} />;
  },
  ul: ({ node, ...props }) => (
    <ul className="mb-3 list-disc space-y-1.5 pl-5 text-[15px] leading-relaxed text-foreground/90 last:mb-0" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="mb-3 list-decimal space-y-1.5 pl-5 text-[15px] leading-relaxed text-foreground/90 last:mb-0" {...props} />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold text-white" {...props} />,
  // The Android app link downloads in place; anything else opens a new tab.
  a: ({ node, children, ...props }) =>
    /\.apk$/.test(props.href || "") ? (
      <ApkLink {...props}>{children}</ApkLink>
    ) : (
      <a className="text-primary underline underline-offset-2" target="_blank" rel="noreferrer" {...props}>
        {children}
      </a>
    ),
  // A paragraph holding only a screenshot would otherwise wrap a button in a <p>.
  img: ({ node, src, alt }) => <HelpImage src={src} alt={alt || ""} />,
  table: ({ node, ...props }) => (
    <div className="mb-3 overflow-hidden rounded-xl border border-border last:mb-0">
      <table className="w-full border-collapse text-left text-sm" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => (
    <th className="bg-muted/40 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground" {...props} />
  ),
  td: ({ node, ...props }) => <td className="border-t border-border px-3 py-2 text-foreground/90" {...props} />,
  code: ({ node, ...props }) => (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[13px] text-foreground" {...props} />
  ),
};

const Markdown = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={COMPONENTS}
  >
    {text}
  </ReactMarkdown>
);

/** Help article text: readable sizes, bold for button names. */
const HelpMarkdown = ({ content }) => (
  <>
    {splitFolds(content).map((part, i) =>
      part.fold ? (
        <details
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className="group mb-3 rounded-xl border border-border bg-card/60 px-3 py-2 last:mb-0"
        >
          <summary className="flex cursor-pointer list-none items-center gap-1.5 text-sm font-medium text-muted-foreground [&::-webkit-details-marker]:hidden">
            <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
            {part.fold}
          </summary>
          <div className="mt-2">
            <Markdown text={part.text} />
          </div>
        </details>
      ) : (
        // eslint-disable-next-line react/no-array-index-key
        <Markdown key={i} text={part.text} />
      ),
    )}
  </>
);

export default HelpMarkdown;
